import * as React from "react";
import {
  Query,
  ResultData,
  SearchPillData,
  ResultHighlight,
} from "../../interfaces";
import { Grid, Typography } from "@mui/material";
import { AppBody } from "../AppBody";
import { useEffect } from "react";
import { apiPost, fetchData } from "../../common/services/ApiClient";

import BankFindResults from "./BankFindResults";
import LinkComponent from "../LinkComponent";
import {
  appHeaderTitle,
  appTitles,
  setInstitutionStatus,
} from "../../common/services/Utilities";
import { getBankFindApiFilters } from "../../common/services/BankFindUtilities";
import SearchPills from "../../containers/SearchPills";
import ShowHideButton from "../ShowHideButton";
import QuickTips from "./BankFindQuickTips";
import DataViz from "./BankFindDataViz";
import { ApiBaseUrl } from "../../config";
import { locationSearchPills } from "../../common/services/LocationUtilities";
import Breadcrumbs from "../../containers/Breadcrumbs";
import HelpButton from "../HelpContent/HelpButton";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import BankFindSearchForm from "../../containers/BankFindSearchForm";

interface Props {
  appState: Query;
  onStartRequest: () => void;
  onResultSuccess: () => void;
  onResultFailure: (error: Error) => void;
  updateUi: (uiField: string, uiValue: string | undefined) => void;
  resetState: () => void;
  dataLoaded: boolean;
}

interface BankFindAppState {
  resultData: ResultData;
  createTimestamp: string;
  recordCount: number;
  resultHighlight: ResultHighlight;
}

const BankfindIntro = (
  <>
    {appHeaderTitle("bankFind")}
    <Typography variant="body2">
      The Name & Location Search allows you to find FDIC-insured banks and
      branches from today, to last year, and all the way back to 1934.
    </Typography>
    <LinkComponent appName={"NameLocation"} />
  </>
);

export default function BankFind({
  appState,
  onStartRequest,
  onResultSuccess,
  onResultFailure,
  updateUi,
  resetState,
  dataLoaded,
}: Props) {
  const [locationValues, setLocationValues] = React.useState("");
  const [currentState, setCurrentState] = React.useState(appState);
  const [prevState, setPrevState] = React.useState(appState);
  const [bankFindAppState, setBankFindAppState] = React.useState<
    BankFindAppState
  >({
    resultData: [],
    createTimestamp: "",
    recordCount: 0,
    resultHighlight: [],
  });
  const [dataDownloadPost, setDataDownloadPost] = React.useState({});

  const locationFormOptions =
    appState.filters.stalp ||
    appState.filters.zip ||
    appState.search.city ||
    appState.filters.countyInst;

  const showBankFindResults =
    Object.keys(appState.filters).some(
      (key: string) => appState.filters[key],
    ) ||
    Object.keys(appState.search).some((key: string) => appState.search[key]);

  const bankfindSearchPills: SearchPillData[] = [
    {
      value:
        JSON.stringify(appState.filters.activeStatus) === '["1"]'
          ? "Active Institutions"
          : JSON.stringify(appState.filters.activeStatus) === '["0"]'
          ? "Inactive Institutions"
          : "Active & Inactive Institutions",
      static: true,
      search: false,
      formKey: "activeStatus",
    },
    {
      value: locationFormOptions
        ? appState.ui.branchOffices
          ? "Include Branch Offices"
          : "Main Office Only"
        : "",
      static: true,
      search: false,
      formKey: "branchOffices",
    },
    {
      label: "Name",
      value: appState.search.name,
      search: true,
      formKey: "name",
    },

    {
      label: "Cert",
      value: appState.filters.cert,
      search: false,
      formKey: "cert",
    },
    {
      label: "County",
      value: appState.filters.county,
      search: false,
      formKey: "county",
    },
    {
      label: "URL",
      value: appState.search.url,
      search: true,
      formKey: "url",
    },
    ...locationSearchPills(appState),
  ];

  const searchPills = (
    <div id="bankfind-search-pills-container">
      <ShowHideButton
        title="Selected Search Criteria"
        body={<SearchPills dataArray={bankfindSearchPills} />}
      />
    </div>
  );

  useEffect(() => {
    setCurrentState({
      ...appState,
      ui: { branchOffices: appState.ui.branchOffices },
      urls: {},
    });
  }, [appState]);

  useEffect(() => {
    // used to set location filters for View Offices Modal
    const locationFilters = [];
    const locationSearch = [];
    if (appState.filters.zip) {
      locationFilters.push(`ZIP:"${appState.filters.zip}"`);
    }
    if (appState.filters.stalp) {
      locationFilters.push(`STALP:"${appState.filters.stalp}"`);
    }
    if (appState.search.city) {
      locationSearch.push(`CITY:${appState.search.city}`);
    }
    if (appState.ui.branchOffices) {
      setLocationValues(
        `${
          locationFilters.length > 0
            ? ` AND ${locationFilters.join(" AND ")}`
            : ""
        }${
          locationSearch.length > 0
            ? `&search=${locationSearch.join(" AND ")}`
            : ""
        }`,
      );
    } else {
      setLocationValues("");
    }
  }, [
    appState.filters.stalp,
    appState.filters.zip,
    appState.search.city,
    appState.ui.branchOffices,
  ]);

  useEffect(() => {
    // resets state to default when navigating to URL with no query params
    if (!window.location.search) {
      resetState();
    }
  }, [resetState]);

  useEffect(() => {
    // remove this from ui so the value is not appended to the URL on level 1
    if (appState.ui.bankfindLevelThreeView) {
      updateUi("bankfindLevelThreeView", undefined);
    }
  }, [appState.ui.bankfindLevelThreeView, updateUi]);

  useEffect(() => {
    // prevents the API from being called when UI updates are made that do not change the API request.
    if (
      JSON.stringify(currentState) !== JSON.stringify(prevState) &&
      appState.urls.browserPath
    ) {
      setPrevState(currentState);
      (async () => {
        onStartRequest();
        const apiFilters: any = await getBankFindApiFilters(appState);

        const locationsPostReq = apiPost(apiFilters);
        typeof apiFilters !== "string"
          ? setDataDownloadPost(apiFilters)
          : setDataDownloadPost({});

        const fetch =
          typeof apiFilters === "string"
            ? fetchData(apiFilters)
            : fetchData(`${ApiBaseUrl()}/institutions`, locationsPostReq);

        await fetch
          .then(response => {
            const formattedInstitutions = response.data.map((data: any) =>
              setInstitutionStatus(data),
            );

            setBankFindAppState({
              resultData: formattedInstitutions,
              recordCount: response.totals.count,
              createTimestamp: response.createTimestamp,
              resultHighlight: response.highlight,
            });
            onResultSuccess();
          })
          .catch((error: Error) => {
            onResultFailure(error);
          });
      })();
    }
  }, [
    appState,
    currentState,
    onResultFailure,
    onResultSuccess,
    onStartRequest,
    prevState,
  ]);

  useInitialPageLoad("bankFind");

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle={appTitles("bankFind")} />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        introText={BankfindIntro}
        searchForm={
          <BankFindSearchForm
            createTimestamp={bankFindAppState.createTimestamp}
          />
        }
        dataViz={<DataViz />}
      >
        {showBankFindResults ? (
          <BankFindResults
            dataDownloadPost={dataDownloadPost}
            appState={appState}
            searchPills={searchPills}
            resultData={bankFindAppState.resultData}
            recordCount={bankFindAppState.recordCount}
            resultHighlight={bankFindAppState.resultHighlight}
            dataLoaded={dataLoaded}
            locationValues={locationValues}
          />
        ) : (
          <QuickTips />
        )}
      </AppBody>
    </>
  );
}
